import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Mailbox, Download } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, fmtDate } from './design';

// Statements waiting to go by post: the participants and supporters who asked for a letter, have no
// email address, or whose email could not be sent. Download them with address labels, post them,
// then record the posting date: that is the proof the statement was given.
export default function StatementLetters({ period, refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [postedOn, setPostedOn] = useState(new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date()));
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/statements/letters', { params: { period } })
      .then((r) => { setRows(r.data || []); setPicked(new Set()); })
      .catch((e) => { setRows([]); setError(e.response?.data?.message || 'Could not load the letters to post'); });
  }, [period]);
  useEffect(() => { load(); }, [load, refreshKey]);
  // A failed load is said, not hidden: hiding the panel would read as "no letters to post".
  if (error) {
    return (
      <Panel title="Letters to post" icon={Mailbox} className="mb-5" bodyClass="px-4 py-3">
        <p role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-red-800">
          {error}
          <button type="button" onClick={load} className="btn-ghost btn-sm">Try again</button>
        </p>
      </Panel>
    );
  }
  if (!rows.length) return null;

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get('/aged-care/statements/letters/download', { params: { period }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `statement-letters-${period}.zip`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Could not build the letters';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const posted = async () => {
    const ids = picked.size ? [...picked] : rows.map((r) => r.id);
    if (!postedOn) return toast.error('Choose the day the letters were posted');
    // Nothing ticked means every letter on the list. The posting date is the proof each statement
    // was given, so recording all of them at once is checked first.
    if (!picked.size) {
      const n = ids.length;
      const ok = await confirm(
        `${n === 1 ? 'The letter is' : `All ${n} letters are`} recorded as posted on ${fmtDate(postedOn)}. That date is the proof the statement was given, so post ${n === 1 ? 'it' : 'them'} first.`,
        { title: n === 1 ? 'Mark the letter posted?' : `Mark all ${n} letters posted?`, confirmLabel: `Mark ${n} posted` },
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await api.post('/aged-care/statements/letters/posted', { ids, postedOn });
      toast.success(`${r.data.posted} letter${r.data.posted === 1 ? '' : 's'} recorded as posted`);
      load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record the posting'); } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <Panel title={`Letters to post (${rows.length})`} icon={Mailbox} className="mb-5" bodyClass="divide-y divide-slate-100"
      actions={(
        <span className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={download}><Download size={13} /> Letters and labels</button>
          <input type="date" aria-label="Posted on" className="input-field !w-auto !py-1 !text-[12px]" value={postedOn} onChange={(e) => setPostedOn(e.target.value)} />
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={posted}>{picked.size ? `Mark ${picked.size} posted` : 'Mark all posted'}</button>
        </span>
      )}>
      {rows.map((r) => (
        <label key={r.id} className="px-4 py-2 text-[13px] flex items-start gap-2 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
          <span className="min-w-0">
            <span className="text-slate-900 font-medium">{r.name}</span>
            <span className="text-slate-500">{r.recipient_kind === 'supporter' ? ` (supporter of ${r.participant})` : ''} · {r.address}</span>
            {r.detail && <span className="block text-amber-800 text-[12px]">{r.detail}</span>}
          </span>
        </label>
      ))}
    </Panel>
  );
}
